import React, { useEffect, useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useToast } from "../../contexts/Toast/ToastProvider";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { useGetTenantDefaultsQuery, useGetTenantSchemaQuery } from "../../graphql/generated/graphql";
import DynamicFormContainer from "./DynamicFormContainer";

interface CreateTenantFormProps {
  onSubmit: (data: any) => void;
  loading?: boolean;
}

function CreateTenantForm({ onSubmit, loading = false }: CreateTenantFormProps) {
  const [tenantSchema, setTenantSchema] = useState(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const { showToast } = useToast();
  const { data: schemaData } = useGetTenantSchemaQuery();
  const { data: tenantDefaultsData, loading: defaultsLoading, error: defaultsError } = useGetTenantDefaultsQuery();

  // Recursively remove __typename entries that Apollo adds for caching
  const stripTypename = (input: any): any => {
    if (Array.isArray(input)) return input.map(stripTypename);
    if (input && typeof input === "object") {
      const { __typename, ...rest } = input as any;
      const cleaned: Record<string, any> = {};
      Object.keys(rest).forEach((key) => {
        cleaned[key] = stripTypename(rest[key]);
      });
      return cleaned;
    }
    return input;
  };

  useEffect(() => {
    if (schemaData?.getTenantSchema?.data) {
      const parsedSchema = JSON.parse(schemaData.getTenantSchema.data);

      // Filter out the id field from the Tenant schema properties, as it is generated by the database
      if (parsedSchema.definitions?.Tenant?.properties?.id) {
        const { id, ...filteredProperties } = parsedSchema.definitions.Tenant.properties;
        parsedSchema.definitions.Tenant.properties = filteredProperties;
      }

      setTenantSchema(parsedSchema);
    }
  }, [schemaData]);

  useEffect(() => {
    if (tenantDefaultsData?.getTenantDefaults?.success && tenantDefaultsData?.getTenantDefaults?.data?.[0]) {
      const defaults = tenantDefaultsData.getTenantDefaults.data[0];
      setValues(stripTypename(defaults));
    }
  }, [tenantDefaultsData]);

  const handleValueChange = (key: string, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!values.tenantName || values.tenantName.trim() === "") {
      showToast({
        message: "Tenant Name is required. Please enter a tenant name.",
        type: "warning",
      });
      return;
    }

    if (values.tenantName !== values.tenantName.toLowerCase()) {
      showToast({
        message: "Tenant Name must be all lowercase.",
        type: "warning",
      });
      return;
    }

    if (!values.tenantDomain || values.tenantDomain.trim() === "") {
      showToast({
        message: "Tenant Domain is required. Please enter a tenant domain.",
        type: "warning",
      });
      return;
    }

    if (values.tenantDomain !== values.tenantDomain.toLowerCase()) {
      showToast({
        message: "Tenant Domain must be all lowercase.",
        type: "warning",
      });
      return;
    }

    if (!values.stripeSecretKey || values.stripeSecretKey.trim() === "") {
      showToast({
        message: "Stripe Secret Key is required. Please enter a valid Stripe secret key.",
        type: "warning",
      });
      return;
    }

    const cleaned = stripTypename(values);
    onSubmit(cleaned);
  };

  if (defaultsLoading || !tenantSchema) {
    return (
      <View style={styles.loadingContainer}>
        <CustomText text="Loading tenant schema and defaults..." />
      </View>
    );
  }

  if (defaultsError) {
    return (
      <View style={styles.errorContainer}>
        <CustomText text={`Error loading tenant defaults: ${defaultsError.message}`} style={styles.errorText} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.formContainer} showsVerticalScrollIndicator={false} tabIndex={0}>
      <DynamicFormContainer
        schema={tenantSchema.definitions.Tenant}
        values={values}
        onValueChange={handleValueChange}
        showSaveButton={false}
      />

      <CustomButton
        text={loading ? "Creating Tenant..." : "Create Tenant"}
        onPress={handleSubmit}
        styleType="primary"
        size="m"
        style={styles.submitButton}
        disabled={loading || defaultsLoading}
      />
      <CustomSpacing type="vertical" size="xl" />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    // @ts-expect-error - maxHeight vh units work on web
    maxHeight: "80vh",
  },
  submitButton: {
    justifyContent: "center",
    marginTop: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  errorText: {
    color: "red",
  },
});

export default CreateTenantForm;
